import { useEffect, useId, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';

const PANEL_HEIGHT = 320;

export interface DropdownOption<TValue extends string = string> {
  value: TValue;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface DropdownProps<TValue extends string> {
  value: TValue | '';
  onChange: (value: TValue) => void;
  options: readonly DropdownOption<TValue>[];
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  searchable?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string | undefined;
}

/**
 * Native `select` cannot carry option descriptions or follow the theme tokens on every browser,
 * so the workspace uses this listbox instead wherever a filter or form picker is needed.
 */
export function Dropdown<TValue extends string>({
  value,
  onChange,
  options,
  id,
  placeholder,
  disabled = false,
  invalid = false,
  searchable = false,
  className = '',
  ...aria
}: DropdownProps<TValue>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!searchable || !needle) return options;
    return options.filter((option) => `${option.label} ${option.description ?? ''}`.toLowerCase().includes(needle));
  }, [options, query, searchable]);

  const selected = options.find((option) => option.value === value) ?? null;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  useEffect(() => {
    if (open) listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const firstEnabled = (from: number, step: number) => {
    for (let index = from; index >= 0 && index < visible.length; index += step) {
      if (!visible[index]?.disabled) return index;
    }
    return -1;
  };

  const openPanel = () => {
    const bottom = rootRef.current?.getBoundingClientRect().bottom ?? 0;
    setDropUp(bottom + PANEL_HEIGHT > window.innerHeight);
    const current = visible.findIndex((option) => option.value === value && !option.disabled);
    setActiveIndex(current >= 0 ? current : firstEnabled(0, 1));
    setOpen(true);
  };

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    setQuery('');
    if (restoreFocus) controlRef.current?.focus();
  };

  const commit = (index: number) => {
    const option = visible[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    close(true);
  };

  const move = (step: number) => {
    const from = activeIndex < 0 ? (step > 0 ? 0 : visible.length - 1) : activeIndex + step;
    const next = firstEnabled(from, step);
    if (next >= 0) setActiveIndex(next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      close(true);
      return;
    }
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openPanel();
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(firstEnabled(0, 1));
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(firstEnabled(visible.length - 1, -1));
    } else if (event.key === 'Enter' || (event.key === ' ' && !searchable)) {
      event.preventDefault();
      commit(activeIndex);
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (open && !rootRef.current?.contains(event.relatedTarget)) close(false);
  };

  return (
    <div className={`dropdown ${className}`.trim()} ref={rootRef} onKeyDown={onKeyDown} onBlur={onBlur}>
      <button
        ref={controlRef}
        type="button"
        id={id}
        role="combobox"
        className={`dropdown__control${invalid ? ' dropdown__control--invalid' : ''}`}
        disabled={disabled}
        aria-controls={listId}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={invalid || undefined}
        aria-label={aria['aria-label']}
        aria-describedby={aria['aria-describedby']}
        onClick={() => (open ? close(false) : openPanel())}
      >
        <span className={selected ? 'dropdown__value' : 'dropdown__placeholder'}>
          {selected?.label ?? placeholder ?? t('common.select')}
        </span>
        <Icon name="chevron" className="icon icon--small directional-icon--down dropdown__caret" />
      </button>

      {open && (
        <div className={`dropdown__panel${dropUp ? ' dropdown__panel--above' : ''}`}>
          {searchable && (
            <div className="dropdown__search">
              <Icon name="search" className="icon icon--small" />
              <input
                ref={searchRef}
                type="text"
                autoComplete="off"
                value={query}
                placeholder={t('common.search')}
                aria-label={t('common.search')}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(-1);
                }}
              />
            </div>
          )}

          <ul ref={listRef} className="dropdown__list" id={listId} role="listbox" aria-label={aria['aria-label']} tabIndex={-1}>
            {visible.map((option, index) => (
              <li
                key={option.value}
                role="option"
                className={`dropdown__option${index === activeIndex ? ' dropdown__option--active' : ''}${option.value === value ? ' dropdown__option--selected' : ''}`}
                data-active={index === activeIndex}
                aria-selected={option.value === value}
                aria-disabled={option.disabled}
                onPointerMove={() => !option.disabled && setActiveIndex(index)}
                onClick={() => commit(index)}
              >
                <span className="dropdown__option-text">
                  <span className="dropdown__option-label">{option.label}</span>
                  {option.description && <small className="dropdown__option-description">{option.description}</small>}
                </span>
                {option.value === value && <Icon name="check" className="icon icon--small dropdown__check" />}
              </li>
            ))}
            {visible.length === 0 && <li className="dropdown__empty">{t('common.noResults')}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
